import React from 'react';
import { CalendarEventResponse } from '@troia-v3/types';
import { Icon, type IconName } from '@/shared/icons'
import { getEventDateParts, getEventLocalDate } from '../utils/eventDate';
import { EventHoverCard } from './EventHoverCard';
interface AgendaViewProps {
  currentDate: Date;
  events: CalendarEventResponse[];
  onEventClick: (event: CalendarEventResponse) => void;
}

interface EventsByDate {
  date: Date;
  events: CalendarEventResponse[];
}

export const AgendaView: React.FC<AgendaViewProps> = ({
  events,
  onEventClick,
}) => {
  // Group events by date
  const groupEventsByDate = (): EventsByDate[] => {
    const grouped: Record<string, CalendarEventResponse[]> = {};

    events.forEach(event => {
      const p = getEventDateParts(event);
      const dateKey = `${p.year}-${p.month}-${p.day}`;

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });

    // Convert to array and sort by date
    const result: EventsByDate[] = Object.entries(grouped).map(([_, events]) => {
      // Data do grupo a partir da PARTE-DATA do 1º evento (all-day = data UTC) —
      // evita o off-by-one de `new Date(startTime)` em fusos negativos.
      const date = getEventLocalDate(events[0]);
      date.setHours(0, 0, 0, 0);

      // Sort events within the day by start time
      events.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

      return { date, events };
    });

    // Sort groups by date
    result.sort((a, b) => a.date.getTime() - b.date.getTime());

    return result;
  };

  const isToday = (date: Date): boolean => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const isTomorrow = (date: Date): boolean => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return (
      date.getDate() === tomorrow.getDate() &&
      date.getMonth() === tomorrow.getMonth() &&
      date.getFullYear() === tomorrow.getFullYear()
    );
  };

  const formatDateHeader = (date: Date): string => {
    if (isToday(date)) return 'Hoje';
    if (isTomorrow(date)) return 'Amanhã';

    const weekDays = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

    return `${weekDays[date.getDay()]}, ${date.getDate()} de ${months[date.getMonth()]}`;
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const formatEventTime = (startTime: string, endTime: string, allDay: boolean): string => {
    if (allDay) return 'Dia inteiro';
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  // Map Google Calendar Color IDs (1-11) to hex colors
  const getEventColor = (colorId?: string, eventType?: string): string => {
    if (eventType === 'birthday') return '#10b981'; // Emerald-500 para aniversários
    if (eventType === 'activity') return '#f97316'; // Orange-500 para atividades

    const colors: Record<string, string> = {
      '1': '#a4bdfc',  // Lavender
      '2': '#7ae7bf',  // Sage
      '3': '#dbadff',  // Grape
      '4': '#ff887c',  // Flamingo
      '5': '#fbd75b',  // Banana
      '6': '#ffb878',  // Tangerine
      '7': '#46d6db',  // Peacock
      '8': '#e1e1e1',  // Graphite
      '9': '#5484ed',  // Blueberry
      '10': '#51b749', // Basil
      '11': '#dc2127', // Tomato
    };
    return colors[colorId || '9'] || colors['9']; // Default to Blueberry
  };

  const getStatusBadge = (status?: string): { text: string; color: string } => {
    const statusMap: Record<string, { text: string; color: string }> = {
      confirmed: { text: 'Confirmado', color: '#10B981' },
      tentative: { text: 'Tentativo', color: '#F59E0B' },
      cancelled: { text: 'Cancelado', color: '#EF4444' },
    };
    return statusMap[status || 'confirmed'] || statusMap.confirmed;
  };

  // Map event type to its catalog icon (mockup typeIcon). 'birthday' has no
  // gift glyph in the catalog → 'star' is the closest existing celebratory icon.
  const getTypeIcon = (eventType?: string): IconName => {
    const icons: Record<string, IconName> = {
      meeting: 'calendar',
      task: 'check-circle',
      reminder: 'circle',
      follow_up: 'corner-up-left',
      activity: 'activity',
      other: 'grid',
      birthday: 'star',
    };
    return icons[eventType || 'meeting'] || 'calendar';
  };

  const groupedEvents = groupEventsByDate();

  if (groupedEvents.length === 0) {
    return (
      <div
        className="h-full flex items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        <div className="text-center px-6">
          <Icon name="calendar" className="h-12 w-12 mx-auto mb-3"
            style={{ color: 'var(--theme-muted-text)' }} />
          <p className="text-base font-semibold" style={{ color: 'var(--theme-card-text)' }}>
            Nenhum evento encontrado
          </p>
          <p className="text-sm mt-1" style={{ color: 'var(--theme-muted-text)' }}>
            Crie um novo evento ou ajuste os filtros
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-4">
        {groupedEvents.map(({ date, events: dayEvents }) => (
          <div key={date.toISOString()} className="mb-1">
            {/* Date header */}
            <h3
              className="sticky top-0 z-[3] py-3 px-0.5 text-sm font-bold capitalize"
              style={{
                backgroundColor: 'var(--theme-page-background)',
                color: isToday(date) ? 'var(--theme-primary)' : 'var(--theme-card-text)',
              }}
            >
              {formatDateHeader(date)}
            </h3>

            {/* Events list */}
            <div className="flex flex-col gap-2">
              {dayEvents.map(event => {
                const statusBadge = getStatusBadge(event.status);
                const cancelled = event.status === 'cancelled';

                return (
                  <EventHoverCard key={event.id} event={event}>
                    <div
                      className="rounded-xl border p-3.5 cursor-pointer transition-colors flex gap-3"
                      style={{
                        borderColor: 'var(--theme-border)',
                        borderLeftWidth: '3px',
                        borderLeftColor: getEventColor(event.colorId, event.eventType),
                        backgroundColor: 'var(--theme-card-background)',
                      }}
                      onClick={() => onEventClick(event)}
                    >
                      {/* Time column */}
                      <div
                        className="flex-none w-16 text-right text-xs font-semibold leading-[1.35] tabular-nums"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {event.allDay || event.eventType === 'birthday' ? (
                          'Dia inteiro'
                        ) : (
                          <>
                            {formatTime(event.startTime)}
                            <br />
                            {formatTime(event.endTime)}
                          </>
                        )}
                      </div>

                      {/* Main content */}
                      <div className="flex-1 min-w-0">
                      {/* Event header */}
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="flex items-start gap-2 flex-1 min-w-0">
                          <div className="mt-0.5 flex-shrink-0" style={{ color: getEventColor(event.colorId, event.eventType) }}>
                            <Icon name={getTypeIcon(event.eventType)} className="h-4 w-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4
                              className="text-sm font-semibold truncate"
                              style={{
                                color: cancelled ? 'var(--theme-muted-text)' : 'var(--theme-card-text)',
                                textDecoration: cancelled ? 'line-through' : undefined,
                              }}
                            >
                              {event.summary}
                            </h4>
                            {event.description && (
                              <p
                                className="text-xs line-clamp-2 mt-0.5"
                                style={{ color: 'var(--theme-muted-text)' }}
                              >
                                {event.description}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Status badge */}
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full font-bold whitespace-nowrap flex-shrink-0"
                          style={{
                            backgroundColor: `${statusBadge.color}20`,
                            color: statusBadge.color,
                          }}
                        >
                          {statusBadge.text}
                        </span>
                      </div>

                      {/* Event details */}
                      <div className="flex flex-wrap gap-x-3 gap-y-1.5 text-[11px] pl-6" style={{ color: 'var(--theme-muted-text)' }}>
                        {/* Time */}
                        <div className="flex items-center gap-1">
                          <Icon name="clock" className="h-3.5 w-3.5" />
                          <span>{formatEventTime(event.startTime, event.endTime, event.allDay)}</span>
                        </div>

                        {/* Location */}
                        {event.location && (
                          <div className="flex items-center gap-1">
                            <Icon name="map-pin" className="h-3.5 w-3.5" />
                            <span>{event.location}</span>
                          </div>
                        )}

                        {/* Recurrence */}
                        {event.recurrence && (
                          <div className="flex items-center gap-1">
                            <Icon name="repeat" className="h-3.5 w-3.5" />
                            <span>Recorrente</span>
                          </div>
                        )}

                        {/* Attendees count */}
                        {event.attendees && event.attendees.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Icon name="users" className="h-3.5 w-3.5" />
                            <span>{event.attendees.length} participantes</span>
                          </div>
                        )}
                      </div>
                      </div>
                    </div>
                  </EventHoverCard>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
